
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CastResponse {
  id: string;
  user_id: string;
  cast_id: string;
  response_text: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    farm_name: string;
    avatar_url: string;
  } | null;
}

export const useCastResponses = (castId: string) => {
  const [responses, setResponses] = useState<CastResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchCastResponses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First, get all cast responses
      const { data: responsesData, error: responsesError } = await supabase
        .from('cast_responses')
        .select('*')
        .eq('cast_id', castId)
        .order('created_at', { ascending: true });

      if (responsesError) {
        console.error('Error fetching cast responses:', responsesError);
        setError(responsesError.message);
        return;
      }

      // If we have responses, get the corresponding profiles
      if (responsesData && responsesData.length > 0) {
        const userIds = responsesData.map(response => response.user_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, farm_name, avatar_url')
          .in('user_id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          setError(profilesError.message);
          return;
        }

        // Combine responses with profiles
        const combinedResponses: CastResponse[] = responsesData.map(response => {
          const profile = profilesData?.find(p => p.user_id === response.user_id);
          return {
            ...response,
            profiles: profile ? {
              username: profile.username || 'unknown',
              farm_name: profile.farm_name || 'Unknown Farm',
              avatar_url: profile.avatar_url || ''
            } : null
          };
        });

        setResponses(combinedResponses);
        console.log(`✅ Successfully fetched ${combinedResponses.length} responses for cast ${castId}`);
      } else {
        setResponses([]);
        console.log(`✅ No responses found for cast ${castId}`);
      }
    } catch (err) {
      console.error('Error fetching cast responses:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const createResponse = async (responseText: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to respond to casts",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase
        .from('cast_responses')
        .insert({
          user_id: user.id,
          cast_id: castId,
          response_text: responseText
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating cast response:', error);
        toast({
          title: "Failed to post response",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Cast response created successfully:', data);
      toast({
        title: "Response posted",
        description: "Your response has been posted successfully"
      });

      // Refresh responses
      await fetchCastResponses();
    } catch (err) {
      console.error('Error creating cast response:', err);
      toast({
        title: "Failed to post response",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  };

  const deleteResponse = async (responseId: string) => {
    try {
      const { error } = await supabase
        .from('cast_responses')
        .delete()
        .eq('id', responseId);

      if (error) {
        console.error('Error deleting cast response:', error);
        toast({
          title: "Failed to delete response",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Cast response deleted successfully');
      toast({
        title: "Response deleted",
        description: "Your response has been deleted"
      });

      // Refresh responses
      await fetchCastResponses();
    } catch (err) {
      console.error('Error deleting cast response:', err);
      toast({
        title: "Failed to delete response",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (castId) {
      fetchCastResponses();
    }
  }, [castId]);

  return {
    responses,
    loading,
    error,
    createResponse,
    deleteResponse,
    fetchCastResponses
  };
};
